import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { SelectField, TextArea, TextField } from '../../common/InputField';
import Button from '../../common/Button';
import {
  AiModel,
  CreatePlanInput,
  PlanInterval,
  priceLabels,
  tokenLimitLabels,
} from '../../../types/plan.types';
import { filter, includes, map } from 'lodash';
import PlansService from '../../../services/plans.service';
import { useAppContext } from '../../../utils/context/app-context';
import { StatusValues, ToastType } from '../../../types/common.types';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import AiChatModelService from '../../../services/ai-chat-models.service';
import Switch from '../../common/Switch';
import { TAIChatModel } from '../../../types/chat-ai-models';
const CreatePlanSchema = Yup.object().shape({
  name: Yup.string().required('Required').min(1).max(100).trim(),
  description: Yup.string().min(1).max(500).required(),
  content: Yup.string().min(1).max(1000),
  planInterval: Yup.string().required(),
  price: Yup.number().min(0).required('Price is required'),
  aiModels: Yup.array().of(
    Yup.object().shape({
      platform: Yup.string().required(),
      modelName: Yup.string().required(),
    })
  ),
  allowedModels: Yup.array().of(Yup.string()).default([]),
  tokenLimit: Yup.number().min(0).default(0),
  status: Yup.string().required(),
});

const CreatePlan = () => {
  const { setToastInfo } = useAppContext();
  const { id } = useParams();
  const [chatAIModels, setChatAIModels] = useState<TAIChatModel[]>([]);
  const [initialValues, setInitialValues] = useState<CreatePlanInput>({
    name: '',
    description: '',
    planInterval: PlanInterval.MONTHLY,
    aiModels: [],
    allowedModels: [],
    tokenLimit: 0,
    content: '',
    price: 0,
    status: StatusValues.ACTIVE,
  });

  const getPlanById = async () => {
    try {
      const response = await PlansService.getPlansById(id ?? '');
      if (response.data.data && response.data.data.plan) {
        const planData = response.data.data.plan;
        setInitialValues({
          name: planData.name,
          planInterval: planData.planInterval ?? PlanInterval.MONTHLY,
          price: planData.price ?? 0,
          aiModels: planData.aiModels ?? [],
          allowedModels: planData.allowedModels ?? [],
          // Falls back to the pre-rename field so plans saved before the
          // token-limit rework prefill with their existing budget.
          tokenLimit: planData.tokenLimit ?? planData.monthlyTokenLimit ?? 0,
          content: planData.content ?? '',
          description: planData.description ?? '',
          status: planData.status ?? StatusValues.ACTIVE,
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in fetching plan.',
      });
    }
  };

  const savePlan = async (values: CreatePlanInput) => {
    try {
      const response = await PlansService.createPlan(values);
      if (response.data.data && response.data.data.plan) {
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      console.log('Error: ', err);
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in creating plan',
      });
    }
  };

  const updatePlan = async (values: CreatePlanInput) => {
    try {
      const response = await PlansService.updatePlan({ ...values, id });
      if (response.data.data && response.data.data.plan) {
        const planData = response.data.data.plan;
        setInitialValues({
          name: planData.name,
          planInterval: planData.planInterval ?? PlanInterval.MONTHLY,
          price: planData.price ?? 0,
          aiModels: planData.aiModels ?? [],
          allowedModels: planData.allowedModels ?? [],
          tokenLimit: planData.tokenLimit ?? planData.monthlyTokenLimit ?? 0,
          content: planData.content ?? '',
          description: planData.description ?? '',
          status: planData.status ?? StatusValues.ACTIVE,
        });
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
        getPlanById();
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      console.log('Error: ', err);
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in updating plan',
      });
    }
  };

  const getActiveAIModels = async () => {
    try {
      const aiModelsResponse = await AiChatModelService.getAll(true);
      if (aiModelsResponse.data.data?.models) {
        setChatAIModels(aiModelsResponse.data.data.models);
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in fetching AI Models',
      });
    }
  };

  useEffect(() => {
    if (id) {
      getPlanById();
    }
  }, [id]);

  // Needed on create as well as edit — the Allowed Models grid is built from
  // the live model catalog, not from what the plan already has.
  useEffect(() => {
    getActiveAIModels();
  }, []);

  const statusItems = [
    {
      id: '1',
      title: 'Active',
      value: StatusValues.ACTIVE,
    },
    {
      id: '2',
      title: 'Inactive',
      value: StatusValues.INACTIVE,
    },
    {
      id: '3',
      title: 'Pending',
      value: StatusValues.PENDING,
    },
    {
      id: '4',
      title: 'Deleted',
      value: StatusValues.DELETED,
    },
  ];

  const setUpdatedAIModels = () => {
    if (!chatAIModels.length) return;
    const aiChatModelNames = map(chatAIModels, 'aiModelName');
    const filteredModels = filter(initialValues.aiModels, (model: AiModel) =>
      includes(aiChatModelNames, model?.modelName)
    );
    const aiModelNames = map(initialValues.aiModels, 'modelName');
    const filteredChatModels = filter(
      chatAIModels,
      (model: TAIChatModel) => !includes(aiModelNames, model?.aiModelName)
    );
    const newAiModels = map(filteredChatModels, (model) => {
      return {
        platform: model.platform,
        modelName: model.aiModelName,
        inputTokens: 0,
        outputTokens: 0,
      };
    });
    setInitialValues({
      ...initialValues,
      aiModels: [...filteredModels, ...newAiModels],
    });
  };

  useEffect(() => {
    setUpdatedAIModels();
  }, [chatAIModels]);

  return (
    <div className="w-full pb-10">
      <div className="border border-[rgba(0,0,0,.15)] rounded-3xl p-10">
        <Formik
          initialValues={initialValues}
          validationSchema={CreatePlanSchema}
          validateOnChange={false}
          validateOnBlur={true}
          enableReinitialize={true}
          onSubmit={(values, { resetForm }) => {
            if (id) {
              updatePlan(values);
            } else {
              savePlan(values);
            }
            resetForm();
          }}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            isValid,
            values,
            setFieldValue,
          }) => {
            return (
              <Form>
                <div className="grid grid-flow-col gap-4">
                  <div className="mb-5">
                    <TextField
                      name="name"
                      label="Name"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.name}
                      required={true}
                      isTouched={touched.name}
                      disabled={Boolean(id)}
                      value={values.name}
                    />
                  </div>
                  <div className="mb-5">
                    <SelectField
                      name="planInterval"
                      label="Plan Interval"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.planInterval}
                      required={true}
                      isTouched={touched.planInterval}
                      items={[
                        { title: 'Weekly', value: PlanInterval.WEEKLY },
                        { title: 'Monthly', value: PlanInterval.MONTHLY },
                        { title: 'Quarterly', value: PlanInterval.QUARTERLY },
                        { title: 'Yearly', value: PlanInterval.YEARLY },
                      ]}
                      disabled={Boolean(id)}
                      value={values.planInterval}
                    />
                  </div>
                  <div className="mb-5">
                    <TextField
                      name="price"
                      label="Price"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.price}
                      required={true}
                      isTouched={touched.price}
                      disabled={Boolean(id)}
                      value={values.price}
                      type="number"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <TextField
                    name="description"
                    label="Description"
                    placeholder=""
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.description}
                    required={true}
                    isTouched={touched.description}
                    value={values.description}
                  />
                </div>

                <div className="mb-5">
                  <TextArea
                    name="content"
                    label="Content"
                    placeholder=""
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.content}
                    isTouched={touched.content}
                    value={values.content}
                  />
                </div>

                <div className="grid grid-flow-col grid-cols-3 gap-4">
                  <div className="mb-5">
                    <SelectField
                      name="status"
                      label="Status"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.status}
                      isTouched={touched.status}
                      value={values.status}
                      items={statusItems}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <TextField
                      name="tokenLimit"
                      label={
                        tokenLimitLabels[values.planInterval] ??
                        'Monthly Token Limit'
                      }
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.tokenLimit}
                      isTouched={touched.tokenLimit}
                      value={values.tokenLimit}
                      type="number"
                    />
                    <p className="mt-2 text-[12px] text-[#666]">
                      Billable tokens allowed per {values.planInterval ?? 'month'}.
                      0 means unlimited.
                    </p>
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block mb-3 sm:text-[14px] text-[#666]">
                    Allowed Models
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {map(chatAIModels, (model) => {
                      const isAllowed = includes(
                        values.allowedModels,
                        model.aiModelName
                      );
                      return (
                        <div className="mb-2" key={model.aiModelName}>
                          <Switch
                            label={model.publicName || model.aiModelName}
                            handleClick={() => {
                              setFieldValue(
                                'allowedModels',
                                isAllowed
                                  ? filter(
                                      values.allowedModels,
                                      (modelName) =>
                                        modelName !== model.aiModelName
                                    )
                                  : [
                                      ...(values.allowedModels || []),
                                      model.aiModelName,
                                    ]
                              );
                            }}
                            active={isAllowed}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    type="button"
                    disabled={!touched || !isValid}
                    onClick={() => handleSubmit()}
                  >
                    {id ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default CreatePlan;
